// Intl puts a narrow no-break space before "am"/"pm".
const NARROW_SPACE = new RegExp("\\u202f", "g");
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isDateOnly(value: string | null | undefined): value is string {
  return typeof value === "string" && DATE_PATTERN.test(value);
}

export function shiftDate(date: string, days: number): string {
  const shifted = new Date(`${date}T12:00:00.000Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

function zoneOffsetMs(instant: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).formatToParts(new Date(instant));
  const read = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour"),
    read("minute"),
    read("second"),
  );
  return asUtc - Math.floor(instant / 1000) * 1000;
}

// The instant a wall-clock date and time ("HH:mm") occurs in the given time zone.
export function zonedInstant(
  date: string,
  time: string,
  timezone: string,
): Date {
  const [year, month, day] = date.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  let instant = guess - zoneOffsetMs(guess, timezone);
  instant = guess - zoneOffsetMs(instant, timezone);
  return new Date(instant);
}

// The instant a calendar date begins in the given time zone.
export function zonedDayStart(date: string, timezone: string): Date {
  return zonedInstant(date, "00:00", timezone);
}

// The value a datetime-local input shows for an instant, in the given time zone.
export function localInputValue(value: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).formatToParts(new Date(value));
  const read = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "00";
  return `${read("year")}-${read("month")}-${read("day")}T${read("hour")}:${read("minute")}`;
}

// The instant a datetime-local input value means in the given time zone.
export function instantFromLocalInput(value: string, timezone: string): Date {
  const [date = "", time = "00:00"] = value.split("T");
  return zonedInstant(date, time, timezone);
}

// The last millisecond of a calendar date in the given time zone.
export function zonedDayEnd(date: string, timezone: string): Date {
  return new Date(zonedDayStart(shiftDate(date, 1), timezone).getTime() - 1);
}

export function dateLabel(
  date: string,
  style: "full" | "long" | "medium" = "long",
): string {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: style,
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00.000Z`));
}

export function timeLabel(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    hour: "numeric",
    hourCycle: "h12",
    minute: "2-digit",
    timeZone: timezone,
  })
    .format(new Date(value))
    .replace(NARROW_SPACE, " ");
}

export function dateTimeLabel(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  })
    .format(new Date(value))
    .replace(NARROW_SPACE, " ");
}
